import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { webColors, webBorderRadius } from '../../../constants/webStyles';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { WebProgressSteps } from '../../../components/web';

// Step Components
import StepCountryWeb from './steps/StepCountry.web';
import StepProfessionWeb from './steps/StepProfession.web';
import StepDetailsWeb from './steps/StepDetails.web';
import StepLocationWeb from './steps/StepLocation.web';
import StepVerifyWeb from './steps/StepVerify.web';
import StepSuccessWeb from './steps/StepSuccess.web';

// Build steps dynamically based on user type
const getSteps = (isDoctor) => {
  const baseSteps = [
    { label: 'Country', component: StepCountryWeb },
    { label: 'Profession', component: StepProfessionWeb },
    { label: 'Details', component: StepDetailsWeb },
  ];

  // Add Location step only for facilities (not doctors)
  if (!isDoctor) {
    baseSteps.push({ label: 'Location', component: StepLocationWeb });
  }

  baseSteps.push({ label: 'Verify', component: StepVerifyWeb });

  return baseSteps;
};

const RegisterScreen = () => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const { isDoctor } = useSelector((state) => state.user);
  const isMobile = useIsMobile();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    country: null,
    profession: null,
    specialization: null,
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    state: '',
    termsAccepted: false,
    otp: '',
    nonce: null,
    latitude: null,
    longitude: null,
  });
  const [isComplete, setIsComplete] = useState(false);

  // Get steps based on user type (facilities get extra Location step)
  const STEPS = getSteps(isDoctor);

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const goToStep = (step) => {
    const maxStep = STEPS.length;
    if (step >= 1 && step <= maxStep) {
      setCurrentStep(step);
    }
  };

  const handleComplete = () => {
    setIsComplete(true);
  };

  // Render success screen if registration complete
  if (isComplete) {
    return <StepSuccessWeb navigation={navigation} />;
  }

  const CurrentStepComponent = STEPS[currentStep - 1].component;

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.logo}
          onPress={() => navigation.navigate('Login')}
        >
          <Text style={styles.logoIcon}>🏥</Text>
          <Text style={styles.logoBlue}>Super</Text>
          <Text style={styles.logoPink}>Medic</Text>
        </TouchableOpacity>
        <View style={styles.headerRight}>
          <Text style={styles.headerText}>
            Already have an account?{' '}
          </Text>
          <TouchableOpacity onPress={() => navigation.navigate('Login')}>
            <Text style={styles.headerLink}>Log in</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Progress Steps */}
      <WebProgressSteps steps={STEPS} currentStep={currentStep} />

      {/* Main Content */}
      <View style={[styles.mainContainer, isMobile && styles.mainContainerMobile]}>
        {/* Left Side - Branding */}
        {!isMobile && (
        <View style={styles.brandSide}>
          <View style={styles.brandContent}>
            <Text style={styles.brandIcon}>🏥</Text>
            <Text style={styles.brandTitle}>
              Join Southeast Asia's Leading Healthcare Jobs Platform
            </Text>
            <Text style={styles.brandSubtitle}>
              Connect with top hospitals and clinics across Malaysia, Singapore, Philippines, and Indonesia.
            </Text>

            <View style={styles.brandFeatures}>
              {[
                'Access thousands of healthcare jobs',
                'Locum, full-time & contract positions',
                'Free to join, apply instantly',
              ].map((feature, index) => (
                <View key={index} style={styles.brandFeature}>
                  <View style={styles.brandFeatureIcon}>
                    <Text style={styles.featureCheck}>✓</Text>
                  </View>
                  <Text style={styles.featureText}>{feature}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
        )}

        {/* Right Side - Form */}
        <ScrollView style={styles.formSide} contentContainerStyle={[styles.formSideContent, isMobile && styles.formSideContentMobile]}>
          <View style={styles.formContainer}>
            <CurrentStepComponent
              formData={formData}
              updateFormData={updateFormData}
              goToStep={goToStep}
              currentStep={currentStep}
              onComplete={handleComplete}
              isDoctor={isDoctor}
            />
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: webColors.background,
    minHeight: '100vh',
  },
  header: {
    backgroundColor: webColors.cardBackground,
    paddingHorizontal: 40,
    paddingVertical: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
  },
  logo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  logoIcon: {
    fontSize: 28,
    marginRight: 4,
  },
  logoBlue: {
    fontSize: 24,
    fontWeight: '700',
    color: webColors.secondary,
  },
  logoPink: {
    fontSize: 24,
    fontWeight: '700',
    color: webColors.primary,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerText: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  headerLink: {
    color: webColors.primary,
    fontWeight: '600',
    fontSize: 14,
  },
  mainContainer: {
    flex: 1,
    flexDirection: 'row',
    minHeight: 'calc(100vh - 140px)',
  },
  mainContainerMobile: {
    flexDirection: 'column',
    minHeight: 'auto',
  },
  brandSide: {
    flex: 1,
    background: 'linear-gradient(135deg, #1e3a5f 0%, #2557a7 50%, #3b82f6 100%)',
    backgroundColor: webColors.secondary,
    padding: 60,
    justifyContent: 'center',
  },
  brandContent: {
    maxWidth: 500,
  },
  brandIcon: {
    fontSize: 64,
    marginBottom: 24,
  },
  brandTitle: {
    fontSize: 36,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 16,
    lineHeight: 44,
  },
  brandSubtitle: {
    fontSize: 18,
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: 28,
    marginBottom: 40,
  },
  brandFeatures: {
    gap: 16,
  },
  brandFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  brandFeatureIcon: {
    width: 32,
    height: 32,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureCheck: {
    color: '#ffffff',
    fontWeight: '600',
  },
  featureText: {
    fontSize: 16,
    color: '#ffffff',
  },
  formSide: {
    flex: 1,
    backgroundColor: webColors.cardBackground,
  },
  formSideContent: {
    padding: 40,
    justifyContent: 'center',
    minHeight: '100%',
  },
  formSideContentMobile: {
    padding: 20,
  },
  formContainer: {
    maxWidth: 480,
    marginHorizontal: 'auto',
    width: '100%',
  },
});

export default RegisterScreen;
